import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useUserStore} from 'entities/user';
import {$api} from 'shared/api';
import {COLORS} from 'shared/consts';

type CookieConsentStatus = {
  version: string;
  accepted: boolean;
  acceptedAt: string | null;
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(11, 20, 35, 0.52);
  backdrop-filter: blur(4px);
`;

const Popup = styled.section`
  width: min(480px, 100%);
  padding: 24px;
  border-radius: 20px;
  background: ${COLORS.BG.ELEVATED.S};
  color: ${COLORS.FG.DEFAULT};
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.24);
`;

const Icon = styled.div`
  display: grid;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  place-items: center;
  border-radius: 14px;
  background: ${COLORS.ACCENT.CONTAINER_DEFAULT};
  font-size: 24px;
`;

const Title = styled.h2`
  margin-bottom: 12px;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
`;

const Description = styled.p`
  margin-bottom: 24px;
  color: ${COLORS.FG.SOFT};
  font-size: 15px;
  line-height: 24px;
`;

const ErrorMessage = styled.p`
  margin: -8px 0 16px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${COLORS.ERROR.CONTAINER_SOFT};
  color: ${COLORS.ERROR.DEFAULT};
  font-size: 14px;
  line-height: 20px;
`;

const AcceptButton = styled.button`
  width: 100%;
  min-height: 48px;
  border-radius: 12px;
  background: ${COLORS.ACCENT.DEFAULT};
  color: ${COLORS.STATIC.WHITE};
  font-size: 16px;
  font-weight: 600;
  transition: background 150ms ease, opacity 150ms ease;

  &:hover:not(:disabled) {
    background: ${COLORS.ACCENT.HOVER};
  }

  &:focus-visible {
    outline: 3px solid ${COLORS.ACCENT.CONTAINER_HOVER};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

export const CookieConsentPopup = () => {
  const userId = useUserStore((state) => state.userInfo?.id);
  const checkedUserId = useRef<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      checkedUserId.current = null;
      setIsOpen(false);
      setError(null);
      return;
    }
    if (checkedUserId.current === userId) return;
    checkedUserId.current = userId;
    setError(null);

    let cancelled = false;
    $api
      .get<CookieConsentStatus>('/users/me/cookie-consent')
      .then(({data}) => {
        if (!cancelled) setIsOpen(!data.accepted);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Не удалось проверить согласие. Попробуйте сохранить его ещё раз.');
          setIsOpen(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleAccept = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await $api.post<CookieConsentStatus>('/users/me/cookie-consent');
      setIsOpen(false);
    } catch {
      setError('Не удалось сохранить согласие. Проверьте соединение и попробуйте снова.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      <Popup role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
        <Icon aria-hidden="true">🍪</Icon>
        <Title id="cookie-consent-title">Мы используем файлы cookie</Title>
        <Description>
          Они помогают платформе работать стабильно, сохранять ваши настройки и становиться удобнее. Нажимая «Хорошо», вы соглашаетесь на использование файлов cookie.
        </Description>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <AcceptButton
          type="button"
          autoFocus
          disabled={isSaving}
          onClick={handleAccept}
        >
          {isSaving ? 'Сохраняем…' : 'Хорошо'}
        </AcceptButton>
      </Popup>
    </Overlay>
  );
};
